import CloseIcon from '@mui/icons-material/Close';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import FlightIcon from '@mui/icons-material/Flight';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import HomeIcon from '@mui/icons-material/Home';
import InfoIcon from '@mui/icons-material/Info';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MenuIcon from '@mui/icons-material/Menu';
import PhonelinkRingIcon from '@mui/icons-material/PhonelinkRing';
import SubjectIcon from '@mui/icons-material/Subject';
import {
    AppBar,
    Box,
    Button,
    Collapse,
    Container,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Toolbar,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { Skeleton } from '@mui/material';
import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import WideLogo from '../../assets/squareLogo.png';
import { productURL } from '../../utils/functions/common';
import { ProductsStore } from '../../utils/stores/ProductsStore';

const FullWidth = () => {
    const productController = useContext(ProductsStore);
    const theme = useTheme();
    const [toursAnchor, setToursAnchor] = useState<null | HTMLElement>(null);
    const [aboutAnchor, setAboutAnchor] = useState<null | HTMLElement>(null);

    const handleToursOpen = (event: React.MouseEvent<HTMLElement>) => {
        setToursAnchor(event.currentTarget);
    };

    const handleToursClose = () => {
        setToursAnchor(null);
    };

    const handleAboutOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAboutAnchor(event.currentTarget);
    };

    const handleAboutClose = () => {
        setAboutAnchor(null);
    };

    return (
        <AppBar position="static" elevation={0} sx={{ backgroundColor: 'white', py: 2 }}>
            <Container maxWidth="xl">
                <Toolbar disableGutters sx={{ justifyContent: 'space-between', minHeight: '75px !important' }}>
                    <Box component={Link} to="/" sx={{ display: 'flex', alignItems: 'center' }}>
                        <img
                            src={WideLogo}
                            alt="GSL Aviation Logo"
                            style={{
                                height: 75,
                                cursor: 'pointer',
                            }}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Button component={Link} to="/" color="inherit" sx={{ color: 'text.primary' }}>
                            Home
                        </Button>
                        {productController?.isLoading ? (
                            <Skeleton width={60} height={30} />
                        ) : (
                            <>
                                <Button
                                    onClick={handleToursOpen}
                                    endIcon={<KeyboardArrowDownIcon />}
                                    color="inherit"
                                    sx={{ color: 'text.primary' }}
                                >
                                    Tours
                                </Button>
                                <Menu
                                    anchorEl={toursAnchor}
                                    open={Boolean(toursAnchor)}
                                    onClose={handleToursClose}
                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'left',
                                    }}
                                    transformOrigin={{
                                        vertical: 'top',
                                        horizontal: 'left',
                                    }}
                                >
                                    {productController?.products?.map((product) => (
                                        <MenuItem
                                            key={product.productCode}
                                            component={Link}
                                            to={productURL(product)}
                                            onClick={handleToursClose}
                                        >
                                            {product.name}
                                        </MenuItem>
                                    ))}
                                </Menu>
                            </>
                        )}
                        <Button
                            onClick={handleAboutOpen}
                            endIcon={<KeyboardArrowDownIcon />}
                            color="inherit"
                            sx={{ color: 'text.primary' }}
                        >
                            About
                        </Button>
                        <Menu
                            anchorEl={aboutAnchor}
                            open={Boolean(aboutAnchor)}
                            onClose={handleAboutClose}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'left',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'left',
                            }}
                        >
                            <MenuItem component={Link} to="/termsandconditions" onClick={handleAboutClose}>
                                Terms &amp; Conditions
                            </MenuItem>
                            <MenuItem component={Link} to="/fleet" onClick={handleAboutClose}>
                                Our Fleet
                            </MenuItem>
                            <MenuItem component={Link} to="/faq" onClick={handleAboutClose}>
                                FAQ
                            </MenuItem>
                        </Menu>
                        <Button component={Link} to="/contactus" color="inherit" sx={{ color: 'text.primary' }}>
                            Contact
                        </Button>
                    </Box>
                </Toolbar>
            </Container>
        </AppBar>
    );
};

const SmallHeader = () => {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [tourIconOpen, setTourIconOpen] = useState(false);
    const [aboutIconOpen, setAboutIconOpen] = useState(false);
    const productController = useContext(ProductsStore);
    const theme = useTheme();

    const handleTourList = () => {
        setTourIconOpen(!tourIconOpen);
    };

    const handleAboutList = () => {
        setAboutIconOpen(!aboutIconOpen);
    };

    const toggleDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
        if (
            event.type === 'keydown' &&
            ((event as React.KeyboardEvent).key === 'Tab' || (event as React.KeyboardEvent).key === 'Shift')
        ) {
            return;
        }
        setDrawerOpen(open);
    };

    return (
        <AppBar position="static" elevation={0} sx={{ backgroundColor: 'white' }}>
            <Container maxWidth="xl">
                <Toolbar disableGutters sx={{ justifyContent: 'space-between', padding: 1.875 }}>
                    <Box component={Link} to="/" sx={{ display: 'flex', alignItems: 'center' }}>
                        <img src={WideLogo} alt="GSL Aviation Logo" style={{ height: 70, cursor: 'pointer' }} />
                    </Box>
                    <IconButton onClick={toggleDrawer(true)} aria-label="Menu Icon" sx={{ color: 'text.primary' }}>
                        <MenuIcon />
                    </IconButton>
                    <Drawer
                        anchor="left"
                        open={drawerOpen}
                        onClose={toggleDrawer(false)}
                        PaperProps={{
                            sx: {
                                minWidth: { xs: '100%', sm: 400 },
                                // Links render as <a>; reset so they never inherit the
                                // browser's default blue/visited-purple link colours.
                                '& a': { color: 'inherit', textDecoration: 'none' },
                            },
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                width: '100%',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                paddingTop: 0.875,
                            }}
                        >
                            <ListItem
                                onClick={toggleDrawer(false)}
                                sx={{ padding: '0 20px 15px 15px', justifyContent: 'space-between' }}
                            >
                                <ListItemIcon>
                                    <img
                                        src={WideLogo}
                                        alt="GSL Aviation logo"
                                        style={{ height: 60, paddingTop: 5, cursor: 'pointer' }}
                                    />
                                </ListItemIcon>
                                <CloseIcon sx={{ opacity: 0.8 }} />
                            </ListItem>
                            <ListItem
                                component={Link}
                                to={'/'}
                                onClick={toggleDrawer(false)}
                                sx={{ padding: '7px 20px' }}
                            >
                                <ListItemIcon>
                                    <HomeIcon color="primary" />
                                </ListItemIcon>
                                <ListItemText primary="Home" />
                            </ListItem>
                            <ListItem onClick={handleTourList} sx={{ padding: '7px 20px' }}>
                                <ListItemIcon>
                                    <FlightIcon color="primary" />
                                </ListItemIcon>
                                <ListItemText primary="Tours" />
                                {tourIconOpen ? <ExpandLess /> : <ExpandMore />}
                            </ListItem>
                            <Collapse in={tourIconOpen} timeout="auto" unmountOnExit>
                                <List component="div" disablePadding>
                                    {productController?.products?.map((product) => (
                                        <ListItem
                                            key={product.productCode}
                                            component={Link}
                                            to={productURL(product)}
                                            onClick={toggleDrawer(false)}
                                            sx={{
                                                pl: 7,
                                                pr: 3.125,
                                                py: 0.85,
                                            }}
                                        >
                                            <ListItemText
                                                primary={product.name}
                                                primaryTypographyProps={{
                                                    sx: { fontSize: 15.5, fontWeight: 400, color: 'text.primary' },
                                                }}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </Collapse>
                            <ListItem onClick={handleAboutList} sx={{ padding: '7px 20px' }}>
                                <ListItemIcon>
                                    <InfoIcon color="primary" />
                                </ListItemIcon>
                                <ListItemText primary="About Us" />
                                {aboutIconOpen ? <ExpandLess /> : <ExpandMore />}
                            </ListItem>
                            <Collapse in={aboutIconOpen} timeout="auto" unmountOnExit>
                                <List component="div" disablePadding>
                                    <ListItem
                                        component={Link}
                                        to={'/fleet'}
                                        onClick={toggleDrawer(false)}
                                        sx={{
                                            paddingLeft: 4.375,
                                            paddingRight: 3.125,
                                            paddingTop: 0.75,
                                            paddingBottom: 0.75,
                                            opacity: 0.9,
                                        }}
                                    >
                                        <ListItemIcon>
                                            <FlightIcon color="primary" />
                                        </ListItemIcon>
                                        <ListItemText primary="Our Fleet" />
                                    </ListItem>
                                    <ListItem
                                        component={Link}
                                        to={'/faq'}
                                        onClick={toggleDrawer(false)}
                                        sx={{
                                            paddingLeft: 4.375,
                                            paddingRight: 3.125,
                                            paddingTop: 0.75,
                                            paddingBottom: 0.75,
                                            opacity: 0.9,
                                        }}
                                    >
                                        <ListItemIcon>
                                            <HelpOutlineIcon color="primary" />
                                        </ListItemIcon>
                                        <ListItemText primary="FAQ" />
                                    </ListItem>
                                    <ListItem
                                        component={Link}
                                        to={'/termsandconditions'}
                                        onClick={toggleDrawer(false)}
                                        sx={{
                                            paddingLeft: 4.375,
                                            paddingRight: 3.125,
                                            paddingTop: 0.75,
                                            paddingBottom: 0.75,
                                            opacity: 0.9,
                                        }}
                                    >
                                        <ListItemIcon>
                                            <SubjectIcon color="primary" />
                                        </ListItemIcon>
                                        <ListItemText primary="Terms &amp; Conditions" />
                                    </ListItem>
                                </List>
                            </Collapse>
                            <ListItem
                                component={Link}
                                to={'/contactus'}
                                onClick={toggleDrawer(false)}
                                sx={{ padding: '7px 20px' }}
                            >
                                <ListItemIcon>
                                    <PhonelinkRingIcon color="primary" />
                                </ListItemIcon>
                                <ListItemText primary="Contact Us" />
                            </ListItem>
                        </Box>
                    </Drawer>
                </Toolbar>
            </Container>
        </AppBar>
    );
};

export default function Header() {
    const theme = useTheme();
    const smallScreen = useMediaQuery(theme.breakpoints.down('md'));

    return (
        <>
            {!smallScreen && <FullWidth />}
            {smallScreen && <SmallHeader />}
        </>
    );
}
